/**
 * AddProtectionDialog
 * Dialog for linking an EPI or EPC to an occupational risk
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import apiClient from '../../../services/apiClient';

interface ProtectionItem {
  id: number;
  nome: string;
  tipoDeRisco?: string;
  risco?: string;
  metodo?: string;
  revisao?: string;
}

interface AddProtectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: {
    epiId?: number | null;
    epcId?: number | null;
    isEffective: boolean;
    notes?: string;
  }) => Promise<void>;
  protectionType: 'EPI' | 'EPC';
}

export const AddProtectionDialog: React.FC<AddProtectionDialogProps> = ({
  open,
  onClose,
  onSubmit,
  protectionType,
}) => {
  const [items, setItems] = useState<ProtectionItem[]>([]);
  const [selectedId, setSelectedId] = useState<number | ''>('');
  const [isEffective, setIsEffective] = useState(true);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    const fetchItems = async () => {
      setLoading(true);
      setError(null);
      try {
        const endpoint = protectionType === 'EPI' ? '/epis' : '/epcs';
        const response = await apiClient.get(endpoint, {
          params: { limit: 200 },
        });
        const data = response.data;
        setItems(
          Array.isArray(data)
            ? data
            : data?.epis || data?.epcs || []
        );
      } catch {
        setError(`Erro ao carregar ${protectionType}s`);
      } finally {
        setLoading(false);
      }
    };

    fetchItems();
    // Reset form
    setSelectedId('');
    setIsEffective(true);
    setNotes('');
  }, [open, protectionType]);

  const handleSubmit = async () => {
    if (!selectedId) return;

    setSubmitting(true);
    setError(null);
    try {
      const selectedItem = items.find(i => i.id === selectedId);
      await onSubmit({
        epiId: protectionType === 'EPI' ? selectedId : null,
        epcId: protectionType === 'EPC' ? selectedId : null,
        isEffective,
        notes: notes || undefined,
        epiName: protectionType === 'EPI' ? selectedItem?.nome : undefined,
        epcName: protectionType === 'EPC' ? selectedItem?.nome : undefined,
      } as any);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Erro ao vincular proteção');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Vincular {protectionType} ao Risco
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
              <InputLabel>Selecione o {protectionType}</InputLabel>
              <Select
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value as number)}
                label={`Selecione o ${protectionType}`}
              >
                {items.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.nome}
                    {item.metodo ? ` (CA: ${item.metodo})` : ''}
                    {item.revisao ? ` (Rev: ${item.revisao})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={isEffective}
                  onChange={(e) => setIsEffective(e.target.checked)}
                />
              }
              label="Eficaz"
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Observações"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedId || submitting}
        >
          {submitting ? <CircularProgress size={20} /> : 'Vincular'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddProtectionDialog;
